import { X, Check, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

interface FilterBadgesProps {
  skillsFilter: string[];
  skillsOperator: "AND" | "OR";
  toggleSkill: (skill: string) => void;
  statusFilter: string[];
  toggleStatus: (status: string) => void;
  functionFilter: string;
  setFunctionFilter: (functionName: string) => void;
  assignedToMe?: boolean;
  setAssignedToMe?: (assigned: boolean) => void;
  clearAllFilters: () => void;
}

export const FilterBadges = ({
  skillsFilter,
  skillsOperator,
  toggleSkill,
  statusFilter,
  toggleStatus,
  functionFilter,
  setFunctionFilter,
  assignedToMe,
  setAssignedToMe,
  clearAllFilters
}: FilterBadgesProps) => {
  const { t } = useTranslation();
  // Get count of active filters
  const getActiveFilterCount = () => {
    return skillsFilter.length + statusFilter.length + (functionFilter ? 1 : 0) + (assignedToMe ? 1 : 0);
  };

  const activeFilters = getActiveFilterCount();
  if (activeFilters === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {skillsFilter.length > 0 && (
        <div className="flex items-center gap-1">
          <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-sm flex items-center gap-1">
            {skillsOperator === "AND" ? (
              <Check className="h-3 w-3" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            {skillsFilter.length} {t("candidates.filters.skillLabel")}
          </div>
        </div>
      )}

      {skillsFilter.map(skill => (
        <div 
          key={`skill-${skill}`} 
          className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-sm flex items-center gap-1"
        >
          {skill}
          <X 
            className="h-3 w-3 cursor-pointer" 
            onClick={() => toggleSkill(skill)} 
          />
        </div>
      ))}

      {statusFilter.map(status => (
        <div 
          key={`status-${status}`} 
          className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 rounded-sm flex items-center gap-1"
        >
          {t("candidates.filters.statusLabel")}: {status}
          <X 
            className="h-3 w-3 cursor-pointer" 
            onClick={() => toggleStatus(status)}
          />
        </div>
      ))}

      {functionFilter && (
        <div 
          className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-sm flex items-center gap-1"
        >
          {t("candidates.filters.functionLabel")}: {functionFilter}
          <X 
            className="h-3 w-3 cursor-pointer" 
            onClick={() => setFunctionFilter("")}
          />
        </div>
      )}

      {assignedToMe && setAssignedToMe && (
        <div 
          className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-1 rounded-sm flex items-center gap-1"
        >
          {t("jobs.assignedToMe", "Assigned to me")}
          <X 
            className="h-3 w-3 cursor-pointer" 
            onClick={() => setAssignedToMe(false)}
          />
        </div>
      )}

      {activeFilters > 0 && (
        <button 
          onClick={clearAllFilters}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          {t("candidates.filters.clearAll")}
        </button>
      )}
    </div>
  );
};
